import { request } from './client'

/* **M-13 — INSPECTION AT THE DOOR.** Sprint 46.

   The product could always pass or reject a room. What it could not do was tell a
   supervisor what he was looking at — which is why inspection has been a web-only act
   until now. See `IInspectionQuery` on the server. */

export interface InspectionLine {
  checklistItemId: string
  code: string
  label: string
  labelAr: string | null
  isMandatory: boolean
  isAnswered: boolean
  isException: boolean
  /** The attendant's own words where he marked an exception. Never translated. */
  note: string | null
  photoCount: number
  requiresPhoto: boolean
  /**
   * How many times this attendant has already failed this item since Saturday, not
   * counting the inspection being made now. Zero on almost every line of almost every
   * room, which is why the sentence is drawn only when it is not.
   */
  priorFailuresThisWeek: number
}

/** Why an inspection cannot be made. The server's words, from `Wire.Of(InspectionBar)`.
 *
 *  A UNION AND NOT A STRING, so that the day a fourth bar is added every screen that
 *  turns one of these into a sentence stops compiling — rather than silently drawing a
 *  supervisor a blank where his reason should be. */
export type InspectionBar = 'NOT_HANDED_IN' | 'NOT_ASSIGNED' | 'OWN_WORK'

export interface InspectionView {
  taskId: string
  roomNumber: string
  roomTypeName: string
  roomTypeNameAr: string | null
  taskTypeCode: string
  taskTypeName: string
  taskTypeNameAr: string | null

  /** Both names when a pair handed it in. */
  attendantName: string
  attendantNameAr: string | null
  submittedAtUtc: string | null
  waitingMinutes: number
  /** First started to handed in. **Null is not zero** — a room recovered from an offline
   *  handset may never have recorded a start. */
  takenMinutes: number | null
  standardMinutes: number | null

  itemsAnswered: number
  itemsTotal: number
  /** **Why the buttons are not there — or null, meaning they are.** Decided by the
   *  server, because none of the three reasons is something a handset could know.
   *
   *  It was a boolean until #144, and it answered only `OWN_WORK` — so the screen drew
   *  Pass and Send back on rooms that had never been handed in, and then told the
   *  supervisor, when the server refused him, that he was inspecting his own work. */
  cannotInspect: InspectionBar | null

  lines: InspectionLine[]
}

/* ── W-50 · WHAT IS WAITING FOR AN INSPECTOR (Sprint 65) ───────────────────────

   The handset has a button — M-12's `nextToInspect`, the longest-waiting room, sent so
   that "Inspect next" is an instruction rather than a menu. The DESK needs a list, because
   the question at the desk is not "what shall I inspect?" but "is this queue under control,
   and where is it stuck?" */

export interface InspectionQueueRow {
  taskId: string
  roomId: string
  roomNumber: string
  floorCode: string
  taskTypeCode: string
  taskTypeName: string
  taskTypeNameAr: string | null
  /** Both names when a pair handed it in. */
  attendantName: string
  attendantNameAr: string | null
  submittedAtUtc: string | null
  waitingMinutes: number
  /** First start to hand-in. **Null is not zero** — a room recovered from an offline
   *  handset may never have recorded a start. */
  takenMinutes: number | null
  standardMinutes: number | null
  /** Lines the attendant marked as something he could not do. The one figure on a row
   *  that predicts a rejection. */
  exceptionCount: number
  /** Why this caller cannot inspect THIS row, or null. Same union as the detail. */
  cannotInspect: InspectionBar | null
}

export interface InspectionQueueView {
  waiting: number
  /** **Null when nothing is waiting, never zero.** M-12's rule, same fact. */
  oldestWaitingMinutes: number | null
  /** How many rows below this caller cannot clear himself. */
  blockedForYou: number
  rows: InspectionQueueRow[]
}

export const fetchInspectionQueue = (
  propertyId: string, options: { signal?: AbortSignal } = {},
) => request<InspectionQueueView>(
  `/properties/${propertyId}/inspection-queue`,
  options.signal ? { signal: options.signal } : {})

export interface InspectionFailureInput {
  checklistItemId: string
  note?: string
}

export const fetchInspection = (
  taskId: string, options: { signal?: AbortSignal } = {},
) => request<InspectionView>(
  `/tasks/${taskId}/inspection`, options.signal ? { signal: options.signal } : {})

/** Pass the room. Nothing is recorded as failed. */
export const passInspection = (taskId: string) =>
  request<void>(`/tasks/${taskId}/inspect`, {
    method: 'POST', body: { passed: true },
  })

/**
 * Send it back. The reason is not optional — somebody is telling a man to do a room
 * again, and a rejection nobody can argue with teaches nobody.
 */
export const sendBackInspection = (
  taskId: string, reason: string, failures: InspectionFailureInput[] = [],
) => request<void>(`/tasks/${taskId}/inspect`, {
  method: 'POST', body: { passed: false, reason, failures },
})
